// kfe-forgot-password.js — the iOS OTP flow (Brief 200).
//
// PORT TARGET: iOS `ForgotPassword.storyboard` + `ForgotPasswordViewController
// .swift` + `ForgotPasswordSuccessViewController.swift`. Both scenes are laid
// out on the storyboard's own 375x667 canvas, so CSS px and iOS points are the
// same unit on a phone and every number below is lifted from the storyboard
// unchanged — the same literal-port approach Brief 121 took for the login xib.
//
// THE FLOW (iOS, mirrored exactly — Rule #9):
//
//   /login, "Forgot Password?" tapped
//     -> empty username: an alert, and nothing else happens  (kfe-login.js)
//     -> GET  /app/check-username  -> has_phone              (kfe-login.js)
//     -> this screen, "choose" stage: e-mail / mobile phone / Confirm
//     -> POST /app/generate-otp    -> data.sent_to
//     -> this screen, "sent" stage: where it went, and Re-login
//     -> /login, username still filled in
//
// The one-time code is then typed into the PASSWORD box on the normal login
// screen. There is no "enter the code" screen on iOS and there is none here.
//
// ---------------------------------------------------------------------------
// FOUR THINGS A READER WILL WANT TO CHECK, BECAUSE THEY LOOK LIKE MISTAKES
// ---------------------------------------------------------------------------
//
// 1. THE BACKDROP IS DARK #1A1A1A, NOT THE ORANGE LOGIN ARTWORK.
//    Brief 200's prose says "both new screens are full-screen on the orange
//    artwork". The storyboard disagrees, and the storyboard is canon for
//    colours (Rule #9): BOTH scenes set the root view's `backgroundColor` to
//    `red/green/blue=0.10196078431372549` = #1A1A1A, there is no
//    `LoginBackground` image view in either scene, and neither `viewDidLoad`
//    adds one. iOS really does leave the artwork behind when the learner
//    taps "Forgot Password?". Shipping the orange here would be the Brief 121
//    login mistake again — meeting the brief's letter and missing the phone.
//    Recorded in docs/200-handover.md; the proof page shows the storyboard
//    beside the capture so the ruling can be re-made with the evidence in view.
//
// 2. SMS AND E-MAIL ARE MUTUALLY EXCLUSIVE, SO `send_via: 3` IS UNREACHABLE.
//    `ForgotPasswordMedium` has a `.both = "3"` case and `confirmClicked:`
//    has a branch for it — but `emailClicked:` sets `smsButton.isSelected =
//    false` and `mobileClicked:` sets `emailButton.isSelected = false`
//    (ForgotPasswordViewController.swift:98-108), so the two can never be
//    selected at once and that branch is dead code on the phone. Brief 200's
//    prose offers "SMS, email, or both"; iOS does not. `_medium()` below keeps
//    the three-way resolution verbatim, which leaves 3 exactly as unreachable
//    here as it is on iOS rather than inventing a control iOS has no string
//    for. The success screen's `default: break` — an empty message label for
//    the `.both` case — is the same dead branch seen from the other side.
//
// 3. CONFIRM'S TOP GAP IS MEASURED FROM THE E-MAIL BUTTON, NOT FROM SMS.
//    The storyboard constrains `confirmButton.top` to `emailButton.bottom`
//    (constraint S4p-k6-OeH), and `setupStyle()` sets that one constant to 82
//    when `has_phone` and 16 when not. With SMS shown that leaves 26pt below
//    it (82 - 16 - 40), not the 16pt every other gap on the screen uses. The
//    asymmetry is canon, not a rounding error.
//
// 4. IT IS ONE COMPONENT WITH THREE STAGES, NOT TWO ROUTES.
//    The storyboard has two view controllers, and Brief 200 scope item 2
//    leaves the choice open. Two reasons this is one component:
//      * iOS presents the success screen MODALLY (`pvc?.present(vc…)` after
//        dismissing the chooser) — it has no URL, no back entry and no deep
//        link, so a route would be inventing navigation iOS does not have;
//      * the success screen's only dynamic content is `sent_to`, a masked
//        e-mail address or phone number. Carrying it to a second route means
//        putting personal data in a query string, which this project does not
//        do.
//    `_stage` is therefore local state, and reloading mid-flow restarts at
//    the username box rather than resurrecting half a flow — which is also
//    what dismissing the modal and starting again does on the phone.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import * as router from "../router.js";
import { crmCheckUsername, crmGenerateOtp, firstFieldError } from "../data/crm-client.js";
import { takeForgotHandoff, setReturningUsername } from "../data/forgot-handoff.js";
import { ASSET_BASE } from "../ui/kfe-splash.js";

/** `ForgotPasswordMedium` (ForgotPasswordViewController.swift:11-15). */
export const SEND_VIA_SMS = 1;
export const SEND_VIA_EMAIL = 2;
export const SEND_VIA_BOTH = 3;

export class KFEForgotPassword extends KFEComponent {
  static styles = `
    /* GEOMETRY IS THE STORYBOARD'S. ForgotPassword.storyboard is authored on
       a 375x667 retina4_7 canvas in points, so the numbers are literal:

         back button      30pt leading, 36pt top (20pt status bar + 16), 28.5x26
         scroll view      30pt side insets -> 315 wide, 78pt top, 20pt bottom
         labels/buttons   full 315 width, 16pt apart
         option rows      40pt tall, #535353, radius 5, 20pt content inset
         Confirm          40pt tall, radius 5, #F87D18 live / #808080 dead
         help text        16pt under Confirm, 30pt of tail below it

       Scene 2 (success): container 30pt inset / 36pt top, message 40pt down,
       then 20pt gaps to the contact line and to Re-login. */
    :host {
      position: fixed; inset: 0;
      display: block;
      /* The root view's own backgroundColor, both scenes. NOT the login
         artwork — see note 1 in the file header. */
      background: #1A1A1A;
      color: #fff;
      font-family: var(--kfe-font);
      /* iOS draws both scenes in IdealSans (Light 14/16, Medium 16/18/24),
         which the web build does not licence or vendor. Every size and weight
         below is the storyboard's; the face is the app's own stack. */
    }

    /* iOS is portrait-phone only, so the storyboard has no opinion about a
       1400px window. Brief 121 settled this for the login screen: keep the
       app column and lay the storyboard out inside it. At or below 480px —
       every phone — this container is the full viewport and the geometry is
       byte-identical. */
    .screen {
      position: absolute; inset: 0;
      max-width: var(--kfe-app-max-width, 480px);
      margin-inline: auto;
      overflow: hidden;
    }

    /* 28.5x26 at 30pt leading, 36pt from the top. The BackBtn image itself is
       8.5x15; the button is wider because of the storyboard's 20pt trailing
       contentEdgeInset, which is tap target, not chrome. */
    button.back {
      position: absolute;
      left: 30px; top: 36px;
      width: 28.5px; height: 26px;
      display: flex; align-items: center; justify-content: flex-start;
      padding: 0; border: 0; background: none;
      cursor: pointer;
    }
    button.back img { width: 8.5px; height: 15px; display: block; }

    /* The scrollView: 30pt insets, under the back button, 20pt off the bottom.
       It scrolls because the storyboard's does — the content is taller than a
       375x667 screen once the help text wraps. */
    .sheet {
      position: absolute;
      left: 30px; right: 30px; top: 78px; bottom: 20px;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
    /* Scene 2 has no back button and no scroll view: a plain container, 36pt
       from the top, 16pt off the bottom. */
    .sheet.sent { top: 36px; bottom: 16px; }

    /* headerLabel — IdealSans-Medium 24. */
    h1 {
      margin: 0;
      font-size: 24px; font-weight: 500; line-height: 24px;
    }
    /* instructionsLabel / helpLabel — IdealSans-Light 14, numberOfLines 0. */
    p.body {
      margin: 16px 0 0;
      font-size: 14px; font-weight: 300; line-height: 1.35;
      white-space: pre-line;
    }
    /* chooseLabel and scene 2's messageLabel — IdealSans-Medium 16. */
    p.lead {
      margin: 16px 0 0;
      font-size: 16px; font-weight: 500; line-height: 1.3;
    }
    /* contactInfoLabel — IdealSans-Light 16, and 20pt down, not 16. */
    p.sent-to {
      margin: 20px 0 0;
      font-size: 16px; font-weight: 300; line-height: 1.3;
      overflow-wrap: anywhere;
    }

    /* The two option rows. Leading-aligned content with a 20pt inset, a 21x21
       checkbox, then the title. iOS gets the gap from two literal spaces in
       the button title ("  e-mail"); the localized string is "e-mail" and the
       gap is layout, so it is a gap here. */
    button.option {
      display: flex; align-items: center;
      box-sizing: border-box;
      width: 100%; height: 40px; margin: 16px 0 0;
      padding: 0 20px;
      border: 0; border-radius: 5px;
      background: #535353; color: #fff;
      font-family: inherit; font-size: 18px; line-height: 1;
      text-align: left;
      cursor: pointer;
    }
    button.option img { width: 21px; height: 21px; margin-right: 8px; flex: none; }

    /* confirmButton and scene 2's reLoginButton: 40pt, radius 5, centred
       IdealSans-Medium 18. Enabled fill is viewDidLoad's rgb(248,125,24) —
       the same value the storyboard sets — and the disabled fill is
       UIColor.gray, #808080. */
    button.primary {
      display: block; box-sizing: border-box;
      width: 100%; height: 40px;
      border: 0; border-radius: 5px;
      background: #F87D18; color: #fff;
      font-family: inherit; font-size: 18px; font-weight: 500;
      cursor: pointer;
    }
    button.primary[disabled] { background: #808080; cursor: not-allowed; }
    /* Confirm's own gap is set inline, because it is state rather than style
       — see _renderForm(). This is the no-SMS value, which is also the
       username stage's. */
    button.confirm { margin-top: 16px; }
    /* Scene 2: 20pt under the contact line, never 16. */
    button.relogin { margin-top: 20px; }
    /* Scene 2's messageLabel is 40pt below the container top, not the 16pt
       every label in scene 1 uses. */
    .sheet.sent p.lead { margin-top: 40px; }

    /* The web-only username box (see _stage === "username"). Styled as the
       login xib's field so the two pre-auth screens agree, since the
       storyboard has no field of its own to copy. */
    input {
      display: block; box-sizing: border-box;
      width: 100%; height: 40px; margin: 16px 0 0;
      border: 0; border-radius: 0;
      background: #000; color: #fff;
      font-family: inherit; font-size: 17px; font-weight: 600;
      text-align: center;
    }
    input::placeholder { color: rgba(255, 255, 255, 0.8); }
    input:focus { outline: 2px solid #fff; outline-offset: -2px; }

    /* iOS raises both failures in a modal KFEAlertView. Brief 188 decided the
       web shows login errors in the page instead, and Brief 200 criterion 5
       says to use the same presentation here — so the alert's WORDS appear in
       the page, directly under Confirm where the layout has room. */
    p.error {
      margin: 16px 0 0; padding: 0.5rem 0.75rem;
      background: rgba(0, 0, 0, 0.85);
      font-size: 14px; line-height: 1.35;
    }

    .tail { height: 30px; }
  `;

  /**
   * "username" — no username reached this screen, so ask for one (web-only:
   *   iOS can only arrive here from the login screen and always carries one).
   * "choose"   — the storyboard's first scene.
   * "sent"     — the storyboard's second scene.
   * @type {"username"|"choose"|"sent"}
   */
  _stage = "username";

  /** @type {string} */ _username = "";
  /** iOS `has_phone`: hides the SMS row when false. @type {boolean} */
  _hasPhone = false;
  /** `emailButton.isSelected`. @type {boolean} */ _emailSelected = false;
  /** `smsButton.isSelected`. @type {boolean} */ _smsSelected = false;
  /** `loadingConfirm()` — disabled, grey, "Loading...". @type {boolean} */ _busy = false;
  /** The server's message, in the page instead of a modal. @type {string} */ _error = "";
  /** `data.sent_to` — the masked address the code went to. @type {string} */ _sentTo = "";
  /** Which medium actually sent, for the success message. @type {1|2|3|0} */ _sentVia = 0;

  onConnected() {
    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("forgot.title") },
      bubbles: true,
      composed: true,
    }));

    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    void this._adopt();
  }

  /**
   * Work out which stage to open on.
   *
   * The username arrives in `?username=` (Brief 200 scope item 1) and
   * `has_phone` in a one-shot handoff the login screen armed beside it —
   * `has_phone` is a server answer about the account, so it is deliberately
   * NOT in the URL where it could be hand-edited into offering an SMS option
   * to an account with no phone.
   *
   * When the handoff is missing or stale — a reload, a bookmark, a hand-typed
   * URL — the username is still in the URL, so rather than dead-ending we do
   * what the login screen would have done and call check-username here. That
   * keeps criterion 3's "called once" true of the tap-through flow (one tap,
   * one call, on the login screen) while making a direct visit work too.
   */
  async _adopt() {
    const fromUrl = (new URLSearchParams(location.search).get("username") ?? "").trim();
    const handoff = takeForgotHandoff();

    if (handoff && fromUrl && handoff.username === fromUrl) {
      this._username = fromUrl;
      this._hasPhone = handoff.hasPhone;
      this._stage = "choose";
      this._scheduleRender();
      return;
    }

    if (!fromUrl) {
      this._stage = "username";
      this._scheduleRender();
      return;
    }

    // A username with no handoff: recover has_phone ourselves.
    this._username = fromUrl;
    this._busy = true;
    this._scheduleRender();
    const result = await crmCheckUsername({ username: fromUrl });
    if (!this.isConnected) return;
    this._busy = false;
    if (result.success) {
      this._hasPhone = result.data?.has_phone === true;
      this._stage = "choose";
    } else {
      // Keep the learner on the username box with the server's words, rather
      // than a chooser that cannot work.
      this._stage = "username";
      this._error = this._messageFor(result.error);
    }
    this._scheduleRender();
  }

  /**
   * The failure copy, in iOS's order of preference:
   *   1. the per-field validation sentence (`errors[0].message[0]`) — what
   *      `checkUsernameWithUsername`'s isFailure block shows;
   *   2. the envelope's `message` — what `generateOTP`'s isFailure shows;
   *   3. iOS's own two client-side strings, for a transport failure that
   *      never reached the server.
   * @param {any} error
   * @returns {string}
   */
  _messageFor(error) {
    if (error?.kind === "network") {
      // iOS splits these: no connectivity at all is
      // `isNetworkAvailable() == false`, a failed round-trip is the `error`
      // branch of the data task. navigator.onLine is the nearest web signal.
      return navigator.onLine === false ? t("forgot.errorOffline") : t("forgot.errorUnreachable");
    }
    return firstFieldError(error) || error?.message || t("forgot.errorUnreachable");
  }

  /** @param {Event} e */
  _onUsernameInput = (e) => {
    this._username = /** @type {HTMLInputElement} */ (e.target).value;
  };

  /**
   * The web-only username step. Runs exactly what the login screen's
   * "Forgot Password?" runs, including the empty-username guard, so the two
   * entry points cannot diverge.
   * @param {Event} e
   */
  _onUsernameSubmit = async (e) => {
    e.preventDefault();
    if (this._busy) return;
    const username = this._username.trim();
    if (!username) {
      this._error = t("login.enterUsername");
      this._scheduleRender();
      return;
    }

    this._busy = true;
    this._error = "";
    this._scheduleRender();

    const result = await crmCheckUsername({ username });
    if (!this.isConnected) return;
    this._busy = false;

    if (result.success) {
      this._username = username;
      this._hasPhone = result.data?.has_phone === true;
      this._stage = "choose";
    } else {
      this._error = this._messageFor(result.error);
    }
    this._scheduleRender();
  };

  /**
   * `emailClicked:` verbatim — toggle self, clear the other
   * (ForgotPasswordViewController.swift:98-102).
   */
  _onEmailClick = () => {
    this._emailSelected = !this._emailSelected;
    this._smsSelected = false;
    this._scheduleRender();
  };

  /** `mobileClicked:` verbatim (…:104-108). */
  _onSmsClick = () => {
    this._smsSelected = !this._smsSelected;
    this._emailSelected = false;
    this._scheduleRender();
  };

  /**
   * `toggleConfirm()` — Confirm is live only once a medium is selected
   * (…:78-84). `_busy` is `loadingConfirm()`, which also disables it.
   * @returns {boolean}
   */
  _confirmEnabled() {
    return !this._busy && (this._emailSelected || this._smsSelected);
  }

  /**
   * `confirmClicked:`'s medium resolution, kept in iOS's own order and with
   * its own default (…:111-124). The `.both` arm cannot be reached while
   * `_onEmailClick` / `_onSmsClick` clear each other — see header note 2.
   * @returns {1|2|3}
   */
  _medium() {
    if (this._emailSelected && !this._smsSelected) return SEND_VIA_EMAIL;
    if (!this._emailSelected && this._smsSelected) return SEND_VIA_SMS;
    if (this._emailSelected && this._smsSelected) return SEND_VIA_BOTH;
    return SEND_VIA_EMAIL; // iOS's `var medium: ForgotPasswordMedium = .email`
  }

  _onConfirm = async () => {
    if (!this._confirmEnabled()) return;
    const sendVia = this._medium();

    this._busy = true;
    this._error = "";
    this._scheduleRender();

    const result = await crmGenerateOtp({ username: this._username, sendVia });
    if (!this.isConnected) return;
    this._busy = false;

    if (result.success) {
      this._sentTo = result.data?.sent_to ?? "";
      this._sentVia = sendVia;
      this._stage = "sent";
    } else {
      // `enableConfirm()` then the alert: the learner can retry (criterion 5).
      this._error = this._messageFor(result.error);
    }
    this._scheduleRender();
  };

  /**
   * `backClicked:` / `reLoginClicked:` — both are just `dismiss(animated:)`,
   * and the thing underneath is the login screen, with the learner's typed
   * username still in its box because iOS never tore that screen down.
   *
   * The web navigated here, so <kfe-login> WAS torn down and the username has
   * to be handed back for criterion 4's last clause to hold. The
   * last-username store cannot do it: that one holds the last SUCCESSFUL
   * login, and a learner in this flow has not had one — on a fresh device it
   * is empty.
   */
  _onBackToLogin = () => {
    setReturningUsername(this._username);
    router.navigate("/login");
  };

  render() {
    if (this._stage === "sent") return this._renderSent();
    return this._renderForm();
  }

  /** Scene 1, plus the web-only username step ahead of it. */
  _renderForm() {
    const choosing = this._stage === "choose";
    /**
     * `confirmButtonTopSpacing` (…:60-64) is 82 with the SMS row shown and 16
     * without — but the constraint it drives is
     * `confirmButton.top = emailButton.bottom + constant`, measured from the
     * E-MAIL row in BOTH cases (header note 3). A CSS margin is measured from
     * whatever precedes it in flow, which with SMS shown is the SMS row, so
     * the 82 has to have the SMS row's own 16 + 40 taken back out of it:
     * 82 - 56 = 26. The canon number is 82 from the e-mail row, and that is
     * what the parity test asserts; 26 is this layout engine's way of saying
     * it.
     */
    const confirmTop = choosing && this._hasPhone ? 82 - (16 + 40) : 16;
    const checkbox = (/** @type {boolean} */ on) =>
      `${ASSET_BASE}/checkbox-${on ? "ticked" : "unticked"}@2x.png`;

    return html`
      <div class="screen">
        <button class="back" type="button" aria-label="${t("forgot.back")}" on:click=${this._onBackToLogin}>
          <img src="${ASSET_BASE}/back-btn@2x.png" alt="" />
        </button>
        <div class="sheet">
          <h1>${t("forgot.title")}</h1>
          <p class="body">${t("forgot.instructions")}</p>

          ${choosing ? html`
            <p class="lead">${t("forgot.sendVia")}</p>
            <button class="option" id="forgot-email" type="button"
                    aria-pressed="${this._emailSelected ? "true" : "false"}"
                    on:click=${this._onEmailClick}>
              <img src="${checkbox(this._emailSelected)}" alt="" />${t("forgot.sendViaEmail")}
            </button>
            ${this._hasPhone ? html`
              <button class="option" id="forgot-sms" type="button"
                      aria-pressed="${this._smsSelected ? "true" : "false"}"
                      on:click=${this._onSmsClick}>
                <img src="${checkbox(this._smsSelected)}" alt="" />${t("forgot.sendViaSms")}
              </button>
            ` : ""}
            <button class="primary confirm" id="forgot-confirm" type="button"
                    style="margin-top: ${confirmTop}px"
                    .disabled=${!this._confirmEnabled()}
                    on:click=${this._onConfirm}>
              ${this._busy ? t("forgot.loading") : t("forgot.confirm")}
            </button>
          ` : html`
            <form on:submit=${this._onUsernameSubmit}>
              <p class="lead">${t("forgot.usernamePrompt")}</p>
              <input id="forgot-username" name="username" type="text" autocomplete="username"
                     placeholder="${t("login.username")}" aria-label="${t("login.username")}"
                     .value=${this._username} .disabled=${this._busy}
                     on:input=${this._onUsernameInput} />
              <button class="primary confirm" id="forgot-continue" type="submit"
                      .disabled=${this._busy}>
                ${this._busy ? t("forgot.loading") : t("forgot.confirm")}
              </button>
            </form>
          `}

          ${this._error ? html`<p class="error" role="alert">${this._error}</p>` : ""}

          <p class="body">${t("forgot.helpIntro")}
${t("forgot.helpDomestic")}
${t("forgot.helpInternational")}</p>
          <div class="tail"></div>
        </div>
      </div>
    `;
  }

  /**
   * Scene 2. `setupStyle()` picks the message off the medium and leaves it
   * EMPTY for `.both` (`default: break`) — mirrored rather than patched,
   * since `.both` is unreachable anyway (header note 2).
   */
  _renderSent() {
    const message =
      this._sentVia === SEND_VIA_EMAIL ? t("forgot.sentToEmail")
      : this._sentVia === SEND_VIA_SMS ? t("forgot.sentToPhone")
      : "";

    return html`
      <div class="screen">
        <div class="sheet sent">
          <p class="lead">${message}</p>
          <p class="sent-to" id="forgot-sent-to">${this._sentTo}</p>
          <button class="primary relogin" id="forgot-relogin" type="button" on:click=${this._onBackToLogin}>
            ${t("forgot.reLogin")}
          </button>
        </div>
      </div>
    `;
  }
}
